import { useMemo } from 'react';
import { GRID_CONFIG, LAYOUT_CONFIG } from '../constants/gridConfig';

/**
 * Custom hook for calculating grid configuration
 * @param {Object} dimensions - Container dimensions
 * @param {Array} allComponents - All components array
 * @returns {Object} Grid configuration object
 */
export const useGridConfig = (dimensions, allComponents) => {
  return useMemo(() => {
    // Early return for invalid dimensions
    if (!dimensions.width || !dimensions.height || allComponents.length === 0) {
      return {
        dividerRatio: LAYOUT_CONFIG.DIVIDER_RATIO,
        mainWidth: 0,
        sideWidth: 0,
        gridCols: 12,
        gridRows: 8,
        rowHeight: 50,
        marginX: LAYOUT_CONFIG.DEFAULT_MARGINS.X,
        marginY: LAYOUT_CONFIG.DEFAULT_MARGINS.Y,
        columnWidth: 100,
        sideColumnWidth: 100
      };
    }

    const dividerRatio = LAYOUT_CONFIG.DIVIDER_RATIO;
    const mainWidth = dimensions.width * dividerRatio;
    const sideWidth = dimensions.width * (1 - dividerRatio);
    
    // More efficient grid dimension calculation using reduce
    const { maxX, maxY } = allComponents.reduce((acc, component) => {
      const x = (component.layout?.x || 0) + (component.layout?.w || 1);
      const y = (component.layout?.y || 0) + (component.layout?.h || 1);
      return {
        maxX: Math.max(acc.maxX, x),
        maxY: Math.max(acc.maxY, y)
      };
    }, { maxX: 1, maxY: 1 });
    
    const gridCols = Math.max(Math.min(maxX, GRID_CONFIG.COLS), 12);
    const gridRows = Math.max(Math.min(maxY, GRID_CONFIG.MAX_ROWS), 8);
    
    const marginX = LAYOUT_CONFIG.DEFAULT_MARGINS.X;
    const marginY = LAYOUT_CONFIG.DEFAULT_MARGINS.Y;
    const rowHeight = Math.max(6, (dimensions.height - (gridRows - 1) * marginY) / gridRows);

    const totalMarginX = (gridCols - 1) * marginX;
    const columnWidth = (mainWidth - totalMarginX) / gridCols;
    const sideColumnWidth = (sideWidth - totalMarginX) / gridCols;

    return {
      dividerRatio,
      mainWidth,
      sideWidth,
      gridCols,
      gridRows,
      rowHeight,
      marginX,
      marginY,
      columnWidth,
      sideColumnWidth
    };
  }, [dimensions.width, dimensions.height, allComponents]);
};
